import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {connect} from "react-redux";

import Popup from "../../../components/Popup";
import IntegrationWizard, {IntegrationWizardStep} from "../../../components/IntegrationWizard";
import {openInNewTab} from "../../../methods/openInNewTab";
import {loadSelf} from "../../../methods/loadSelf";

import "pages/Styles/IntegrationsWizard.sass";

type Props = {
    close: Function,
    $store: any,
    $commitToStore: Function,
}

type WizardProgress = {
    current_step_index: number
    completed_step_ids: string[]
}

enum MAXBotStatus {
    awaitingConfirmation = "AWAITING_CONFIRMATION",
    confirmed = "CONFIRMED"
}

const FLOW_ID = "max_notifications_setup";

function storageKeyForUser(userSession: any) {
    const userId = userSession?._id || "unknown";
    const employeeId = userSession?.employee_id || "owner";
    return `${FLOW_ID}:${userId}:${employeeId}`;
}

function safeParseProgress(raw: string | null): WizardProgress | null {
    if (!raw) return null;
    try {
        const parsed = JSON.parse(raw);
        if (!parsed || typeof parsed !== "object") return null;
        const current_step_index = Number(parsed.current_step_index);
        const completed_step_ids = Array.isArray(parsed.completed_step_ids) ? parsed.completed_step_ids.filter(Boolean) : [];
        if (!Number.isFinite(current_step_index)) return null;
        return {current_step_index, completed_step_ids};
    } catch {
        return null;
    }
}

let MaxIntegrationSetupPopup: any = function MaxIntegrationSetupPopup(props: Props) {
    const userSession = props.$store.userSession;
    const [currentStepIndex, setCurrentStepIndex] = useState<number>(0);
    const [completedStepIds, setCompletedStepIds] = useState<Set<string>>(new Set());

    const lastSavedRef = useRef<string>("");

    function maxContacts() {
        return userSession?.max_contacts ?? [];
    }

    function hasConfirmedMaxContact() {
        const currentEmployeeId = userSession?.employee_id;
        if (currentEmployeeId) {
            return maxContacts().some((contact: any) =>
                contact &&
                contact.status === MAXBotStatus.confirmed &&
                contact.created_by_employee_id === currentEmployeeId
            );
        }
        return maxContacts().some((contact: any) =>
            contact &&
            contact.status === MAXBotStatus.confirmed &&
            !contact.created_by_employee_id
        );
    }

    const maxBotId = process.env.REACT_APP_MAX_ENTREPRENEUR_NOTIFICATIONS_BOT_NAME || "id6330100935_bot";
    const maxBotLink = `https://max.ru/${maxBotId}`;

    const steps: IntegrationWizardStep[] = useMemo(() => ([
        {
            id: "max_has_account",
            type: "info",
            title: "Шаг 1. У меня уже есть аккаунт в MAX",
            description: "Если у вас еще нет аккаунта, зарегистрируйтесь/войдите в MAX и вернитесь сюда.",
        },
        {
            id: "max_open_bot",
            type: "external_link",
            title: "Шаг 2. Переход в MAX",
            description: "Откройте чат с нашим ботом в MAX.",
            ctaLabel: "Открыть MAX бота",
            linkUrl: maxBotLink,
        },
        {
            id: "max_share_contact",
            type: "custom",
            title: "Шаг 3. Поделитесь контактом",
            description: "В чате с ботом нажмите кнопку «Выслать свой номер телефона» и отправьте контакт.",
            hint: "Важно: не отправляйте номер телефона текстом — нужно именно поделиться контактом через кнопку.",
            customKey: "max_share_contact",
        },
        {
            id: "max_done",
            type: "custom",
            title: "Шаг 4. Готово",
            description: "Уведомления будут приходить в MAX.",
            customKey: "max_done",
        },
    ]), [maxBotLink]);

    const isStepComplete = useCallback((stepId: string) => {
        if (stepId === "max_share_contact" || stepId === "max_done") {
            return hasConfirmedMaxContact();
        }
        return completedStepIds.has(stepId);
    }, [completedStepIds, userSession]);

    const completeStep = useCallback((stepId: string) => {
        setCompletedStepIds((prev) => {
            if (prev.has(stepId)) return prev;
            const next = new Set(prev);
            next.add(stepId);
            return next;
        });
    }, []);

    const refreshMaxStatus = useCallback(async () => {
        await loadSelf(props);
    }, [props]);

    useEffect(() => {
        const key = storageKeyForUser(userSession);
        const saved = safeParseProgress(localStorage.getItem(key));
        if (!saved) return;
        setCurrentStepIndex(Math.min(saved.current_step_index ?? 0, steps.length - 1));
        setCompletedStepIds(new Set(saved.completed_step_ids ?? []));
    }, []);

    useEffect(() => {
        const key = storageKeyForUser(userSession);
        const progress: WizardProgress = {
            current_step_index: Math.min(currentStepIndex, steps.length - 1),
            completed_step_ids: Array.from(completedStepIds),
        };
        const serialized = JSON.stringify(progress);
        if (serialized === lastSavedRef.current) return;
        lastSavedRef.current = serialized;
        try {
            localStorage.setItem(key, serialized);
        } catch {
            // ignore
        }
    }, [currentStepIndex, completedStepIds, steps.length, userSession]);

    return (
        <Popup
            maxWidth="650px"
            onClose={() => props.close()}
        >
            <div className="realty-integration-setup ui-kit-v2">
                <div className="commonLabel ui-kit-v2 realty-integration-setup__title">
                    Уведомления в MAX
                </div>
                <div className="commonSubtitle ui-kit-v2 realty-integration-setup__subtitle">
                    Подключение уведомлений в формате пошаговой инструкции.
                </div>

                <IntegrationWizard
                    steps={steps}
                    currentStepIndex={currentStepIndex}
                    isStepComplete={isStepComplete}
                    onCompleteStep={completeStep}
                    onChangeStep={setCurrentStepIndex}
                    onFinish={() => props.close()}
                    renderCustomStep={(step) => {
                        if (step.customKey === "max_share_contact") {
                            const done = hasConfirmedMaxContact();
                            return (
                                <>
                                    {step.description && (
                                        <div className="commonSubtitle ui-kit-v2 integration-wizard__description">
                                            {step.description}
                                        </div>
                                    )}
                                    {step.hint && (
                                        <div className="integration-wizard__warning ui-kit-v2">
                                            {step.hint}
                                        </div>
                                    )}
                                    <button
                                        className="action-button secondary ui-kit-v2 integration-wizard__action-button"
                                        onClick={async () => {
                                            await refreshMaxStatus();
                                            if (hasConfirmedMaxContact()) {
                                                completeStep("max_share_contact");
                                            }
                                        }}
                                    >
                                        <span className="button-text">
                                            {done ? "✅ Подключено" : "Проверить статус"}
                                        </span>
                                    </button>

                                    <button
                                        className="action-button secondary ui-kit-v2 integration-wizard__action-button"
                                        style={{marginTop: 10}}
                                        onClick={() => {
                                            setTimeout(() => openInNewTab(maxBotLink), 200);
                                        }}
                                    >
                                        <span className="button-text">Открыть MAX бота</span>
                                    </button>
                                </>
                            );
                        }

                        if (step.customKey === "max_done") {
                            const done = hasConfirmedMaxContact();
                            return (
                                <>
                                    <div className="commonSubtitle ui-kit-v2 integration-wizard__description">
                                        {done
                                            ? "✅ MAX бот подключен. Уведомления будут приходить в MAX."
                                            : "Пока не видим подтверждение. Убедитесь, что вы нажали кнопку и отправили контакт (не текстом), затем вернитесь на шаг 3 и нажмите «Проверить статус»."}
                                    </div>
                                </>
                            );
                        }

                        return null;
                    }}
                />
            </div>
        </Popup>
    );
}

MaxIntegrationSetupPopup = connect(
    (store) => ({ $store: store }),
    (dispatch) => ({
        $commitToStore: (data: any) => dispatch({ ...data, type: 'S' })
    })
)(MaxIntegrationSetupPopup);

export default MaxIntegrationSetupPopup;

